import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  IntersectionItem,
  ProfileStatistics,
  RmmNotificationProfile,
  RmmNotificationProfileComputerCount,
  RmmNotificationProfileComputerCountList,
  RmmNotificationProfileList,
  RmmProfileApplyTo
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { Observable } from 'rxjs';
import { ConfigurationService } from '../../../index';

@Injectable()
export class RmmNotificationService {
  private mbsStageKey: string;
  private settingsApiUrl: string;
  private getProfileApiUrl: string;

  constructor(private http: HttpClient, @Inject(ConfigurationService) private config: ConfigurationService) {
    this.settingsApiUrl = `${this.config.get('rmmCustomSettings')}/api/custom/provider/profile`;
    this.getProfileApiUrl = `${this.config.get('rmmCustomSettings')}/api/custom/public`;
    this.mbsStageKey = this.config.get('mbsStageKey');
  }

  getProfileList(httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<RmmNotificationProfileList> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfileList>(`${this.settingsApiUrl}/${product}/${type}/list`, { params: httpParams });
  }

  getProfileQuantity(httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<RmmNotificationProfileComputerCountList> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfileComputerCountList>(`${this.settingsApiUrl}/${product}/${type}/computers-count`, {
      params: httpParams
    });
  }

  registerProfileSettings(payload: any, httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<RmmNotificationProfile> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.post<RmmNotificationProfile>(`${this.settingsApiUrl}/${product}/${type}/register`, payload, { params: httpParams });
  }

  updateProfileSettings(
    id: string,
    payload: RmmNotificationProfile,
    httpParams: any = {},
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfile> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.put<RmmNotificationProfile>(`${this.settingsApiUrl}/${product}/${type}/${id}`, payload, { params: httpParams });
  }

  removeProfileSettings(id: string, httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<unknown> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.delete<unknown>(`${this.settingsApiUrl}/${product}/${type}/${id}`, { params: httpParams });
  }

  removeProfilesSettings(list: string[], httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<unknown> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.post<unknown>(`${this.settingsApiUrl}/${product}/${type}/delete`, list, { params: httpParams });
  }

  getProfileSettings(
    profileId: string,
    httpParams: any = {
      include: 'notifications'
    },
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfile> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfile>(`${this.settingsApiUrl}/${product}/${type}/${profileId}`, { params: httpParams });
  }

  getProfileCounts(
    profileId: string,
    httpParams: any = {
      include: 'notifications'
    },
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfileComputerCount> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfileComputerCount>(`${this.settingsApiUrl}/${product}/${type}/${profileId}/computers-count`, {
      params: httpParams
    });
  }

  changeProfileState(
    profileId: string,
    disabled: boolean,
    httpParams: any = {},
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfile> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.put<RmmNotificationProfile>(
      `${this.settingsApiUrl}/${product}/${type}/${profileId}/disable`,
      { disabled },
      {
        params: httpParams
      }
    );
  }

  getIntersectionData(httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<IntersectionItem[]> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<IntersectionItem[]>(`${this.settingsApiUrl}/${product}/${type}/intersection`, { params: httpParams });
  }

  checkIntersectionData(
    applyTo: RmmProfileApplyTo,
    httpParams: any = {},
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<IntersectionItem> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.post<IntersectionItem>(`${this.settingsApiUrl}/${product}/${type}/intersection`, applyTo, {
      params: httpParams
    });
  }

  postResolveIntersection(payload: any, httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<unknown> {
    return this.http.post<unknown>(`${this.settingsApiUrl}/${product}/${type}/resolve-intersections`, payload, { params: httpParams });
  }

  getUsedProfileForComputer(
    providerId: string,
    hid: string,
    httpParams: any = {},
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfile> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfile>(`${this.getProfileApiUrl}/${product}/${type}/${providerId}/${hid}/profile`, {
      params: httpParams
    });
  }

  getProfileStatistic(httpParams: any = {}, product = 'rmm', type = 'alertsettings'): Observable<ProfileStatistics> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<ProfileStatistics>(`${this.settingsApiUrl}/${product}/${type}/statistic`, { params: httpParams });
  }

  getCompanyProfileList(
    companyId: string,
    httpParams: any = {},
    product = 'rmm',
    type = 'alertsettings'
  ): Observable<RmmNotificationProfile[]> {
    if (this.mbsStageKey) httpParams.mbsStageKey = this.mbsStageKey;
    return this.http.get<RmmNotificationProfile[]>(`${this.settingsApiUrl}/${product}/${type}/company/${companyId}`, {
      params: httpParams
    });
  }
}
